import React, { useEffect, useRef, useState } from "react";
import { StyledCollaborators } from "./Collaborators.styled";
import { DoubleStructure } from "../../components/core/DoubleStructure/DoubleStructure";
import { TextSwitch } from "../../components/TextSwitch/TextSwitch";
import { BodyTyp } from "../../components/Typography/Typography";
import {
  AlignmentEnum,
  FilterProps,
  FontWeightEnum,
  PageNames,
} from "../../types/AppTypes";
import {
  CANCEL_LABEL,
  PERFORMANCE_COACH_LABEL,
  CC_LABEL,
  COLLABORATOR_SELECTED_LABEL,
  COLLABORATORS_LABEL,
  COLLABORATORS_SELECTED_LABEL,
  CONFIRM_DOWNLOAD_LABEL,
  DOWNLOAD_CV_LABEL,
  DOWNLOAD_FORMAT_LABEL,
  EMPTY_COLLABORATORS_MESSAGE,
  EMPTY_STRING,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
  FORM_STATUS_CM_LABEL,
  NAME_SURNAME_EMAIL_LABEL,
  PRACTICE_LABEL,
  VERIFICATION_STATUS_LABEL,
} from "../../constants/Dictionary";
import { getCollaboratorsTableColumns } from "../../constants/TableColumns";
import { useLocation, useNavigate } from "react-router-dom";
import { FiltersPanel } from "../../components/FiltersPanel/FiltersPanel";
import SearchInput from "../../components/core/SearchInput/SearchInput";
import {
  COLLABORATORS_PAGE_SIZE,
  CollaboratorTypeEnum,
  DOWNLOAD_FORMAT,
  DownloadFormatEnum,
  PermissionsEnum,
  VERIFICATION_STATUS_COLORS,
  VERIFICATION_STATUS_LABELS,
  VerificationStatusEnum,
} from "../../constants/AppConstants";
import { useDebounce } from "use-debounce";
import { getCollaboratorsFiltersTitles } from "../../constants/FilterTitles";
import { useAppSelector } from "../../hooks/store.hook";
import { useDispatch } from "react-redux";
import { FiltersSelected } from "../../components/FiltersSelected/FiltersSelected";
import { Table } from "../../components/core/Table/Table";
import { useBreadCrumb } from "../../hooks/useBreadCrumbs.hooks";
import { Collaborator } from "../../types/api/collaborators.type";
import {
  setFiltersCollaboratorActive,
  setSelectedTab,
  setSearchCollaboratorActive,
  setSearchCollaboratorFollow,
  setPaginationCollaboratorActive,
  setPaginationCollaboratorFollow,
} from "../../stores/actions/collaborator.actions";
import { getCollaboratorsTabs } from "../../utils/collaborators.utils";
import {
  DETAIL_QUERY_PARAM_KEY,
  encodeParams,
} from "../../utils/queryParams.utils";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../components/core/Button/Button";
import { ReactComponent as FileCheckIcon } from "../../icons/fileCheck.svg";
import { roleHasPermission } from "../../utils/roleAuth.utils";
import { ModalSelectRadio } from "../../components/ModalSelectRadio/ModalSelectRadio";
import { useNotification } from "../../hooks/notification.hook";
import CollaboratorsCurriculumsService from "../../services/api/collaborators/CollaboratorsCurriculums.service";
import { useDimensions } from "../../hooks/useDimensions.hook";
import { TextBoxGrid } from "../../components/TextBoxGrid/TextBoxGrid.component";
import {
  findCCById,
  findPracticeById,
  findStatusLabelByValue,
} from "../../utils/orgFunctions.utils";
import { VerificationStatusButton } from "../../components/VerificationStatusButton/VerificationStatusButton";
import {
  useCollaborators,
  useCollaboratorsFilters,
} from "../../hooks/collaborators.hook";

export const Collaborators = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notification } = useNotification();
  const { pathname } = useLocation();
  const [checkboxEnabled, setCheckboxEnabled] = useState(false);
  const [cvToDownload, setCvToDownload] = useState<Collaborator[]>([]);
  const [isOpenFormatDownloadModal, setIsOpenFormatDownloadModal] =
    useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);

  const {
    filtersCollaborator,
    selectedTab,
    searchCollaborator,
    paginationCollaborator,
  } = useAppSelector((state) => state.collaborator);
  const { profile } = useAppSelector((state) => state.user);
  const { orgSettings } = useAppSelector((state) => state.config);

  const tabValue = selectedTab?.value;

  const { isMobile } = useDimensions();

  const { data: filtersData, isLoading: filterLoading } =
    useCollaboratorsFilters(tabValue === CollaboratorTypeEnum.ACTIVE);

  const joinFilters = Object.values(filtersData ?? []).flat();

  const [debounceSearchCollaboratorsFollow] = useDebounce(
    searchCollaborator.FOLLOW,
    500,
  );
  const [debounceSearchCollaboratorsActive] = useDebounce(
    searchCollaborator.ACTIVE,
    500,
  );

  const getDebounceSearchValue = () => {
    if (tabValue === CollaboratorTypeEnum.FOLLOW)
      return debounceSearchCollaboratorsFollow;
    if (tabValue === CollaboratorTypeEnum.ACTIVE)
      return debounceSearchCollaboratorsActive;
  };

  const getPageNumber = () => {
    if (tabValue === CollaboratorTypeEnum.ACTIVE)
      return paginationCollaborator.ACTIVE;
    return paginationCollaborator.FOLLOW;
  };

  const collaboratorIds = cvToDownload.map((c) => c.id);

  const handleDownloadCv = async (format: string) => {
    if (!collaboratorsData) return;

    try {
      const params = [
        { key: "collaboratorIds", value: collaboratorIds },
        { key: "fileExtensions", value: [format] },
      ];
      await CollaboratorsCurriculumsService.get(params);
      CollaboratorsCurriculumsService.download(params);
    } catch (error) {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    } finally {
      setIsOpenFormatDownloadModal(false);
      setDownloading(false);
      setCvToDownload([]);
      setCheckboxEnabled(false);
    }
  };

  const { data: collaboratorsData, isFetching: collaboratorsFetching } =
    useCollaborators({
      paginationCollaborators: getPageNumber() ?? 0,
      enabled: !!tabValue,
      queryParams: [
        ...(tabValue && tabValue !== CollaboratorTypeEnum.FOLLOW
          ? (filtersCollaborator[tabValue] ?? [])
          : []),
        ...(tabValue && tabValue !== CollaboratorTypeEnum.ACTIVE
          ? [{ key: "collaboratorType", value: [tabValue] }]
          : []),
        { key: "search", value: [getDebounceSearchValue() ?? ""] },
      ],
    });

  const handlePagination = (pag: number) => {
    if (tabValue === CollaboratorTypeEnum.ACTIVE)
      dispatch(setPaginationCollaboratorActive(pag));
    if (tabValue === CollaboratorTypeEnum.FOLLOW)
      dispatch(setPaginationCollaboratorFollow(pag));
  };

  const handleFilters = (filters: FilterProps[]) => {
    handlePagination(0);

    dispatch(setFiltersCollaboratorActive(filters));
  };

  useBreadCrumb([
    {
      name: COLLABORATORS_LABEL,
      pathName: PageNames.COLLABORATORS,
    },
  ]);

  useEffect(() => {
    const tabs = getCollaboratorsTabs(profile);
    if (tabs.length > 0 && selectedTab === null) {
      dispatch(setSelectedTab(tabs[0]));
    }
  }, [selectedTab, profile]);

  const filtersAreActive =
    tabValue === CollaboratorTypeEnum.ACTIVE &&
    filtersCollaborator.ACTIVE!.length > 0;

  const valueSearchInput = () => {
    if (tabValue === CollaboratorTypeEnum.FOLLOW)
      return searchCollaborator.FOLLOW;
    if (tabValue === CollaboratorTypeEnum.ACTIVE)
      return searchCollaborator.ACTIVE;
  };

  const onChangeSearchInput = (value: string) => {
    if (tabValue === CollaboratorTypeEnum.FOLLOW)
      dispatch(setSearchCollaboratorFollow(value));
    if (tabValue === CollaboratorTypeEnum.ACTIVE)
      dispatch(setSearchCollaboratorActive(value));
  };

  const isFirstRender = useRef(true);

  const handleRowSelect = (collaborator: Collaborator, checked: boolean) => {
    setCvToDownload((prev) => {
      if (checked) {
        return prev.some((i) => i.id === collaborator.id)
          ? prev
          : [
              ...prev,
              {
                ...collaborator,
              },
            ];
      }
      return prev.filter((i) => i.id !== collaborator.id);
    });
  };

  const handleSelectAll = (rows: Collaborator[], checked: boolean) => {
    if (!checked) {
      const pageIds = new Set(rows.map((r) => r.id));
      setCvToDownload((prev) => prev.filter((c) => !pageIds.has(c.id)));
      return;
    }
    setCvToDownload((prev) => {
      const existingIds = new Set(prev.map((c) => c.id));
      return [...prev, ...rows.filter((r) => !existingIds.has(r.id))];
    });
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!tabValue) return;
    handlePagination(0);
  }, [debounceSearchCollaboratorsFollow, debounceSearchCollaboratorsActive]);

  const canSeeDownloadCvButton = (): boolean => {
    if (tabValue === CollaboratorTypeEnum.FOLLOW) {
      return true;
    }

    if (tabValue === CollaboratorTypeEnum.ACTIVE) {
      return roleHasPermission(
        profile,
        PermissionsEnum.CAN_DOWNLOAD_CV_ACTIVE_RESOURSES,
      );
    }

    return false;
  };

  const calcTableHeight = (): string => {
    let defaultHeight = 365;

    if (checkboxEnabled) {
      defaultHeight += 70;
    }

    if (filtersAreActive) {
      defaultHeight += 52;
    }

    return `calc(100dvh - ${defaultHeight}px)`;
  };

  useEffect(() => {
    setCheckboxEnabled(false);
    setCvToDownload([]);
  }, [tabValue]);

  const expandedRow = (data: Collaborator): React.ReactElement | null => {
    const orgArea = orgSettings.areaList.all;
    const orgCm = orgSettings.cmsheet;
    const verificationLabel =
      VERIFICATION_STATUS_LABELS[data.verificationStatus];
    const verificationColor =
      VERIFICATION_STATUS_COLORS[data.verificationStatus];
    return (
      <TextBoxGrid
        numCol={2}
        items={[
          {
            label: VERIFICATION_STATUS_LABEL,
            text: data.verificationStatus ? (
              <VerificationStatusButton
                label={verificationLabel}
                color={verificationColor}
              />
            ) : (
              <BodyTyp variant="2" fontWeight={FontWeightEnum.REGULAR}>
                {EMPTY_STRING}
              </BodyTyp>
            ),
          },
          ...(tabValue === CollaboratorTypeEnum.ACTIVE
            ? [
                {
                  label: PERFORMANCE_COACH_LABEL,
                  text: data.careerManagerFullName,
                },
              ]
            : []),
          {
            label: PRACTICE_LABEL,
            text: findPracticeById(orgArea, data.practice)?.name,
          },
          {
            label: CC_LABEL,
            text: findCCById(orgArea, data.cc)?.name,
          },
          {
            label: FORM_STATUS_CM_LABEL,
            text: data.cmSheetStatus
              ? findStatusLabelByValue(orgCm, data.cmSheetStatus)
              : EMPTY_STRING,
          },
        ]}
      />
    );
  };

  return (
    <>
      <StyledCollaborators>
        <div className="collaborators-header-container">
          <DoubleStructure
            verticalAlignment={AlignmentEnum.CENTER}
            variant="primary"
          >
            <TextSwitch
              selectedOption={selectedTab}
              setSelectedOption={(option) => {
                dispatch(setSelectedTab(option));
              }}
              options={getCollaboratorsTabs(profile)}
            />
            {canSeeDownloadCvButton() && (
              <Button
                label={DOWNLOAD_CV_LABEL}
                size={ButtonSizes.SMALL}
                icon={<FileCheckIcon />}
                onClick={() => setCheckboxEnabled(true)}
                disabled={checkboxEnabled}
              />
            )}
          </DoubleStructure>
          <div className="filters-column">
            <div className="filters-row">
              <DoubleStructure
                verticalAlignment={AlignmentEnum.CENTER}
                variant="primary"
              >
                <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                  {collaboratorsData &&
                    `${collaboratorsData?.pagination.total} ${selectedTab?.label}`}
                </BodyTyp>
                <div className="search-container">
                  <SearchInput
                    value={valueSearchInput()}
                    onChange={(value) => {
                      onChangeSearchInput(value);
                    }}
                    label={NAME_SURNAME_EMAIL_LABEL}
                    name={"search"}
                  />
                </div>
              </DoubleStructure>
              {tabValue !== CollaboratorTypeEnum.FOLLOW && (
                <FiltersPanel
                  data={filtersData}
                  setSelectedFilters={handleFilters}
                  selectedFilters={filtersCollaborator.ACTIVE!}
                  filtersTitles={getCollaboratorsFiltersTitles(tabValue!)}
                  loading={filterLoading}
                />
              )}
            </div>
            {selectedTab?.value !== CollaboratorTypeEnum.FOLLOW && (
              <FiltersSelected
                data={joinFilters}
                filters={filtersCollaborator.ACTIVE!}
                setFilters={handleFilters}
              />
            )}
          </div>
        </div>
        <div className="collaborators-content">
          <Table<Collaborator>
            loading={collaboratorsFetching}
            rowKey={"id"}
            headers={getCollaboratorsTableColumns(
              selectedTab?.value as CollaboratorTypeEnum,
              orgSettings.areaList.all,
              orgSettings.cmsheet,
              isMobile,
            )}
            scrollY={calcTableHeight()}
            data={collaboratorsData?.data ?? []}
            clickableRow={(record) =>
              !profile.restrictedCollaboratorIds.includes(record.id)
            }
            onRow={(record) => {
              navigate(
                `${pathname}/${record.id}?${DETAIL_QUERY_PARAM_KEY}=${encodeParams(
                  {
                    collaboratorName: record.name,
                    collaboratorSurname: record.surname,
                  },
                )}`,
              );
            }}
            pagination={{
              current: getPageNumber() + 1,
              onChange: (pageNumber) => handlePagination(pageNumber - 1),
              total: collaboratorsData?.pagination.total ?? 0,
              pageSize: COLLABORATORS_PAGE_SIZE,
            }}
            emptyText={EMPTY_COLLABORATORS_MESSAGE}
            onCheckbox={checkboxEnabled ? handleRowSelect : undefined}
            selectedRows={cvToDownload}
            onSelectAll={handleSelectAll}
            isCheckboxDisabled={(row) =>
              row.verificationStatus !== VerificationStatusEnum.VERIFIED ||
              profile.restrictedCollaboratorIds.includes(row.id)
            }
            expandable={
              isMobile ? { expandedRowRender: expandedRow } : undefined
            }
            showRowAction={!isMobile}
          />
        </div>
        {checkboxEnabled && (
          <div className="collaborators-footer">
            <div>
              {`${cvToDownload.length} ${
                cvToDownload.length === 1
                  ? COLLABORATOR_SELECTED_LABEL
                  : COLLABORATORS_SELECTED_LABEL
              }`}
            </div>
            <div className="buttons-container">
              <Button
                label={CANCEL_LABEL}
                variant={ButtonVariants.SECONDARY}
                onClick={() => {
                  setCheckboxEnabled(false);
                  setCvToDownload([]);
                }}
              />
              <Button
                label={CONFIRM_DOWNLOAD_LABEL}
                disabled={cvToDownload.length === 0}
                onClick={() => setIsOpenFormatDownloadModal(true)}
              />
            </div>
          </div>
        )}
      </StyledCollaborators>
      <ModalSelectRadio<string>
        label={DOWNLOAD_FORMAT_LABEL}
        isOpen={isOpenFormatDownloadModal}
        radioSelectionItems={[
          DownloadFormatEnum.PDF,
          DownloadFormatEnum.WORD,
          DownloadFormatEnum.PPT,
        ]}
        onConfirm={(format: string | null) => {
          setDownloading(true);
          if (format) {
            const apiFormat = DOWNLOAD_FORMAT[format as DownloadFormatEnum];
            handleDownloadCv(apiFormat);
          }
        }}
        onCancel={() => {
          setIsOpenFormatDownloadModal(false);
          setCheckboxEnabled(false);
          setCvToDownload([]);
        }}
        isLoading={downloading}
      />
    </>
  );
};
